import { type ILogger } from '@trimble/logging';
import { type PointCloudRoot } from './feature-extraction-types';
import { type FeatureExtractionContextType } from '@mobile-mapping/providers';
import { Accessor, batch, createEffect } from 'solid-js';
import { type LonLatToSceneConverter } from '@trimble/web3d-providers';
import { type PointCloudCrsAuthoring } from '@trimble/geospatial';

export interface CreatePointCloudCrsAuthoringParams {
  readonly context: FeatureExtractionContextType;
  readonly logger: ILogger;
  readonly csib: string;
  readonly pointCloudRoots: Accessor<PointCloudRoot[]>;
  ensureConverter(): Promise<LonLatToSceneConverter | undefined>;
}

export interface PointCloudCrsAuthoringHandle {
  getPointCloudCrsAuthoring(): PointCloudCrsAuthoring | undefined;
  dispose(): void;
}

export function createPointCloudCrsAuthoring(
  params: CreatePointCloudCrsAuthoringParams
): PointCloudCrsAuthoringHandle {
  const { context, logger, csib, pointCloudRoots, ensureConverter } = params;
  // Point-cloud CRS authoring is enabled only once context exposes a CRS and
  // the converter is ready. Bulk feature fetches overflowed Datadog, so this
  // trusts the shared CRS instead of validating against loadAllFeatures.
  let pointCloudCrsAuthoring: PointCloudCrsAuthoring | undefined;
  let disposed = false;

  const enablePointCloudCrsAuthoring = async (
    targetCrs: string
  ): Promise<void> => {
    const converter = await ensureConverter();
    if (!converter || disposed) {
      return;
    }
    if (context.pointCloudCrs() !== targetCrs) {
      return;
    }
    pointCloudCrsAuthoring = {
      toCoordinates: (x, y, z) => [x, y, z],
      crsUrn: targetCrs,
    };
    batch(() => {
      pointCloudRoots()
        .filter((root) => root.csib === csib)
        .forEach((root) => {
          context.setPointCloudCrs(root.id, targetCrs);
        });
    });
    logger.info('Point cloud CRS authoring enabled', { targetCrs });
  };

  createEffect(() => {
    const targetCrs = context.pointCloudCrs();
    if (!targetCrs || disposed) {
      return;
    }
    void enablePointCloudCrsAuthoring(targetCrs);
  });

  return {
    getPointCloudCrsAuthoring: () => pointCloudCrsAuthoring,
    dispose: () => {
      if (disposed) {
        return;
      }
      disposed = true;
    },
  };
}
